import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { getCachedUser } from '../lib/auth'

/**
 * Keepers — succession made concrete (v0.19.0, D-116).
 * A family archive should not depend on one person staying reachable. Naming a
 * second keeper is the whole feature: no timers, no dead-man's switch that can
 * fire while someone is simply on holiday.
 */
export default function KeepersPanel({ treeId, treeName }: { treeId: string; treeName: string }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.treeOwners>> | null>(null)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'idle' | 'confirm'>('idle')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const [keepTo, setKeepTo] = useState('')
  const [keepMsg, setKeepMsg] = useState('')
  const [keepBusy, setKeepBusy] = useState(false)

  const sendKeepsake = async () => {
    setKeepBusy(true); setKeepMsg('')
    try {
      const r = await api.sendKeepsake(treeId, keepTo.trim())
      setKeepMsg(`Sent to ${r.sent_to} — they now hold a copy that outlives this service.`)
      setKeepTo('')
    } catch (e) {
      setKeepMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Could not send it')
    } finally { setKeepBusy(false) }
  }
  const me = getCachedUser()

  const load = () => api.treeOwners(treeId).then(setData).catch(() => {})
  useEffect(() => { load() }, [treeId]) // eslint-disable-line react-hooks/exhaustive-deps

  const isOwner = data?.my_role === 'owner'
  const myPending = data?.owners.find(o => o.user_id === me?.id && o.status === 'pending')

  const startInvite = async () => {
    if (!email.includes('@')) return
    setBusy(true); setMsg('')
    try {
      await api.requestStepUp(me?.email || '')
      setStage('confirm')
      setMsg(`We emailed a 6-digit code to ${me?.email}. Naming a keeper is worth the extra step.`)
    } catch { setMsg('Could not send the confirmation code — try again.') }
    finally { setBusy(false) }
  }

  const confirmInvite = async () => {
    setBusy(true); setMsg('')
    try {
      await api.inviteCoKeeper(treeId, email.trim(), code.trim())
      setMsg(`Asked ${email.trim()} to become a keeper. They'll see it next time they open the tree.`)
      setEmail(''); setCode(''); setStage('idle'); load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Could not name them')
    } finally { setBusy(false) }
  }

  const answer = async (action: 'accept' | 'decline') => {
    setBusy(true)
    try { await api.answerKeeperInvite(treeId, action); load() }
    finally { setBusy(false) }
  }

  if (!data) return null

  return (
    <div className="card-glass p-5 mb-4">
      <h3 className="font-display text-base font-semibold text-slate-100 mb-1">🕯️ Keepers of this constellation</h3>
      <p className="text-xs text-slate-500 mb-4">
        An archive shouldn't depend on one person. Keepers share full responsibility for “{treeName}”, so it
        survives anyone becoming unreachable.
      </p>

      {myPending && (
        <div className="rounded-xl border border-gold-500/30 bg-gold-500/[0.06] p-3 mb-4">
          <p className="text-sm text-slate-200 mb-2">You've been asked to help keep this family history.</p>
          <div className="flex gap-2">
            <button onClick={() => answer('accept')} disabled={busy} className="btn-gold !py-1.5 !px-3 text-xs">Accept</button>
            <button onClick={() => answer('decline')} disabled={busy} className="btn-ghost !py-1.5 !px-3 text-xs">Not now</button>
          </div>
        </div>
      )}

      <div className="space-y-1.5 mb-4">
        {data.owners.map(o => (
          <div key={o.id} className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-white/[0.07] px-3 py-2">
            <span className="text-sm text-slate-300">
              {o.name || o.email}
              {o.is_primary ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-gold-500/15 text-gold-300">primary</span> : null}
              {o.status === 'pending' ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-500/20 text-slate-400">invited</span> : null}
            </span>
            {o.user_id === me?.id && o.status === 'active' && data.owners.filter(x => x.status === 'active').length > 1 && (
              <span className="text-[10px] text-slate-600">you</span>
            )}
          </div>
        ))}
      </div>

      {isOwner && data.owners.length < data.max_owners && (
        stage === 'idle' ? (
          <div className="flex flex-wrap gap-2">
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Their email (must already be on this tree)"
              className="input-dark !py-2 text-sm flex-1 min-w-[220px]" />
            <button onClick={startInvite} disabled={busy || !email.includes('@')} className="btn-ghost !py-2 text-sm">
              {busy ? 'Sending…' : 'Name a keeper'}
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code" inputMode="numeric" autoFocus
              className="input-dark !py-2 text-sm w-40 text-center tracking-[0.3em] font-mono" />
            <button onClick={confirmInvite} disabled={busy || code.length < 6} className="btn-gold !py-2 text-sm">Confirm</button>
            <button onClick={() => { setStage('idle'); setCode(''); setMsg('') }} className="btn-ghost !py-2 text-sm">Cancel</button>
          </div>
        )
      )}
      {msg && <p className="text-xs text-gold-300 mt-3">{msg}</p>}

      {isOwner && (
        <div className="mt-5 pt-4 border-t border-white/[0.07]">
          <h4 className="font-display text-sm font-semibold text-slate-100 mb-1">📦 Send someone a copy to keep</h4>
          <p className="text-xs text-slate-500 mb-3">
            A GEDCOM file in a relative's inbox is the only kind of succession that survives everything —
            it opens in Ancestry, FamilySearch or Gramps, and it still works if this website one day does not.
          </p>
          <div className="flex flex-wrap gap-2">
            <input value={keepTo} onChange={e => setKeepTo(e.target.value)} placeholder="Their email"
              className="input-dark !py-2 text-sm flex-1 min-w-[200px]" />
            <button onClick={sendKeepsake} disabled={keepBusy || !keepTo.includes('@')} className="btn-ghost !py-2 text-sm">
              {keepBusy ? 'Sending…' : 'Send a copy'}
            </button>
          </div>
          {keepMsg && <p className="text-xs text-gold-300 mt-2">{keepMsg}</p>}
        </div>
      )}
    </div>
  )
}
